import {
  createChart,
  type IChartApi,
  type ISeriesApi,
  type LineData,
  type LogicalRange,
  type MouseEventParams,
  type UTCTimestamp,
} from 'lightweight-charts';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  Alert,
  AlertVariants,
  formattedDate,
  FormControlSizes,
  IndicatorBadge,
  IndicatorBadgeVariants,
  InlineFormattedNumber,
  logger,
  Spinner,
  Stack,
  useElementSize,
  useEventListener,
  useSecurity,
  type IOrderConfigurationSnapshot,
  type OrderAnalytic,
} from '@talos/kyoko';

import { ChartLegend, ChartLegendItem, ChartLegendLabel, ChartLegendValue, ChartMeta, ChartWrapper } from './styles';

import { useFeatureFlag } from '@src/hooks';
import type { AnalyticsResolutionEnum } from '@src/utils/getAnalyticsResolution';
import { Text } from '@talos/kyoko';
import { last } from 'lodash-es';
import type { BehaviorSubject, Observable } from 'rxjs';
import { useTheme } from 'styled-components';
import {
  colors,
  createSeries,
  ensureSeriesSpacing,
  getChartRange,
  setChartOptions,
  TrajectoryZoomMode,
  type OrderTrajectoryZoom,
  type TrajectorySeries,
} from './chart';
import { useTrajectoryOrder, type TrajectoryOrderInput } from './trajectoryOrder';
import { useBoundPredictions, type PredictedBoundsPoint } from './useBoundPredictions';

// Chart expects _localized_ unix epoch in seconds
function formatTimeForChart(inDate: string | number): UTCTimestamp {
  const date = new Date(inDate);
  const offset = date.getTimezoneOffset() * 60 * 1000;
  return Math.floor((date.getTime() - offset) / 1000) as UTCTimestamp;
}

interface OrderTrajectoryChartCommonProps {
  order: TrajectoryOrderInput;
  height: number;
  width?: number;
  // If specified, reflow will resize the graph
  resizeAware?: boolean;
  latestAnalytics: OrderAnalytic;
  requestedResolution?: AnalyticsResolutionEnum;
  legOrParentAnalyticsObservable: Observable<{
    data: Record<number, OrderAnalytic[]>;
    initial?: boolean | undefined;
  }>;
  legIndex?: number;
  zoomSubject: BehaviorSubject<OrderTrajectoryZoom>;
  onZoomChange: (zoom: Partial<OrderTrajectoryZoom>) => void;
  /** Hide the Exec Qty series and legend item (e.g., for PreTrade where there's no actual execution) */
  hideExecQty?: boolean;
}

interface OrderTrajectoryChartDynamicProps extends OrderTrajectoryChartCommonProps {
  /** Required when staticBounds is not provided */
  configSnapshot: IOrderConfigurationSnapshot;
  staticBounds?: undefined;
}

interface OrderTrajectoryChartStaticProps extends OrderTrajectoryChartCommonProps {
  /** Pre-computed prediction bounds (e.g., from PreTrade). */
  staticBounds: PredictedBoundsPoint[];
  configSnapshot?: never;
}

export type OrderTrajectoryChartProps = OrderTrajectoryChartDynamicProps | OrderTrajectoryChartStaticProps;

function isStaticPredictionMode(props: OrderTrajectoryChartProps): props is OrderTrajectoryChartStaticProps {
  return props.staticBounds !== undefined;
}

interface LegendValues {
  upperQtyBound?: number;
  execQty?: number;
  lowerQtyBound?: number;
  predExecQty?: number;
}

export const OrderTrajectoryChart = ({
  height,
  width,
  resizeAware = false,
  order,
  latestAnalytics,
  requestedResolution,
  legOrParentAnalyticsObservable,
  legIndex = 0,
  zoomSubject,
  onZoomChange,
  hideExecQty = false,
  ...predictionModeProps
}: OrderTrajectoryChartProps) => {
  const staticBounds = 'staticBounds' in predictionModeProps ? predictionModeProps.staticBounds : undefined;
  const predictionProps: OrderTrajectoryChartProps = {
    height,
    width,
    resizeAware,
    order,
    latestAnalytics,
    requestedResolution,
    legOrParentAnalyticsObservable,
    legIndex,
    zoomSubject,
    onZoomChange,
    hideExecQty,
    ...predictionModeProps,
  };
  const {
    elementRef: ref,
    size: { clientWidth },
  } = useElementSize<HTMLDivElement>();
  const [chart, setChart] = useState<IChartApi | undefined>(undefined);
  const [resolution, setResolution] = useState('');
  const [timestamp, setTimestamp] = useState('');
  const [series, setSeries] = useState<TrajectorySeries>({});
  const [legendValues, setLegendValues] = useState<LegendValues>({});
  const lastValues = useRef<LegendValues>({});
  const isMovingCrosshair = useRef(false);
  const symbol = latestAnalytics?.Symbol || order.Symbol;
  const security = useSecurity(symbol);
  const currency = order.Currency ?? security?.PositionCurrency;
  const theme = useTheme();

  useEffect(() => {
    const chart = createChart(ref.current as HTMLDivElement);
    setChart(chart);
    setSeries(createSeries(chart));
    return () => chart.remove();
  }, [ref]);

  useEffect(() => {
    if (chart && security) {
      setChartOptions(chart, theme, security, height, width);
    }
  }, [chart, security, height, width, theme]);

  const onVisibleRangeChange = useCallback(
    (range: LogicalRange | null) => {
      if (!range) {
        return;
      }

      onZoomChange({
        visibleRange: range,
        updateLegIndex: legIndex,
        mode: TrajectoryZoomMode.None,
      });
    },
    [legIndex, onZoomChange]
  );

  const trajOrder = useTrajectoryOrder(order);
  const { enableOrderTrajectoryPredictions } = useFeatureFlag();

  const {
    canPredict,
    bounds: predBounds,
    shouldInvalidate: shouldInvalidatePred,
    isLoading: isPredLoading,
    error: predError,
  } = useBoundPredictions(
    isStaticPredictionMode(predictionProps)
      ? {
          enable: enableOrderTrajectoryPredictions,
          order: trajOrder,
          staticBounds: predictionProps.staticBounds,
        }
      : {
          enable: enableOrderTrajectoryPredictions,
          order: trajOrder,
          configSnapshot: predictionProps.configSnapshot,
        }
  );

  const updateZoom = useCallback(
    (chartSeries: TrajectorySeries, handleZoomRange: boolean) => {
      if (!chart) {
        return;
      }
      const zoom = zoomSubject.getValue();

      switch (zoom.mode) {
        case TrajectoryZoomMode.ZoomClip: {
          const { from, to } = getChartRange(chartSeries);
          if (!from || !to || !trajOrder.clipInterval) {
            return;
          }

          const start = to - trajOrder.clipInterval / 1000;

          if (start < from) {
            chart.timeScale().setVisibleRange({
              from,
              to: (from + trajOrder.clipInterval / 1000) as UTCTimestamp,
            });
          } else {
            chart.timeScale().setVisibleRange({
              from: start as UTCTimestamp,
              to,
            });
          }

          break;
        }

        case TrajectoryZoomMode.ZoomFit: {
          chart.timeScale().fitContent();
          break;
        }

        case TrajectoryZoomMode.None: {
          if (handleZoomRange && zoom.updateLegIndex !== legIndex && zoom.visibleRange) {
            chart.timeScale().setVisibleLogicalRange(zoom.visibleRange);
          }
          break;
        }
      }
    },
    [chart, legIndex, trajOrder.clipInterval, zoomSubject]
  );

  const isPredAccurateRef = useRef<boolean>(true);
  useEffect(() => {
    if (chart != null && security != null) {
      let predExecQty: LineData[] | undefined;

      if (predBounds) {
        // Align pred bounds to analytics bucket end-times during live streaming (1s resolution)
        const tsOffset = staticBounds ? 0 : 1000;

        predExecQty = predBounds
          .filter(b => Number.isFinite(b.target))
          .map(b => ({ time: formatTimeForChart(b.ts + tsOffset), value: b.target }));
        series.predExecQty?.setData(predExecQty);

        // Only set prediction bound lines in dynamic mode (live order details);
        // in static mode (PreTrade), the analytics series already includes the bounds
        if (!staticBounds) {
          const predLowerQtyBound = predBounds
            .filter(b => Number.isFinite(b.lb))
            .map(b => ({ time: formatTimeForChart(b.ts + tsOffset), value: b.lb }));
          const predUpperQtyBound = predBounds
            .filter(b => Number.isFinite(b.ub))
            .map(b => ({ time: formatTimeForChart(b.ts + tsOffset), value: b.ub }));
          series.predLowerQtyBound?.setData(predLowerQtyBound);
          series.predUpperQtyBound?.setData(predUpperQtyBound);
        } else {
          // The chart instance is reused across prop changes; clear previously rendered dynamic
          // prediction bounds so stale dashed lines do not persist in static-bounds mode.
          series.predLowerQtyBound?.setData([]);
          series.predUpperQtyBound?.setData([]);
        }
      }

      const subscription = legOrParentAnalyticsObservable.subscribe(json => {
        const analytics = json.data[legIndex];
        if (!analytics) {
          return null;
        }

        setResolution(last(analytics)?.Resolution || '');

        const endTime = last(analytics)?.EndTime;
        if (endTime) {
          setTimestamp(formattedDate(endTime) ?? '');
        }

        if (!isPredAccurateRef.current || shouldInvalidatePred(analytics)) {
          isPredAccurateRef.current = false;
          predExecQty = undefined;
          series.predLowerQtyBound?.setData([]);
          series.predUpperQtyBound?.setData([]);
          series.predExecQty?.setData([]);
        }

        const lowerQtyBound = analytics.map(a => ({
          time: formatTimeForChart(a.EndTime),
          ...(a.LowerBound && a.LowerBound !== '0' && { value: parseFloat(a.LowerBound) }),
        }));
        const upperQtyBound = analytics.map(a => ({
          time: formatTimeForChart(a.EndTime),
          ...(a.UpperBound && a.UpperBound !== '0' && { value: parseFloat(a.UpperBound) }),
        }));
        const execQty = analytics.map(a => ({
          time: formatTimeForChart(a.EndTime),
          ...(a.CumQty && a.CumQty !== '0' && { value: parseFloat(a.CumQty) }),
        }));

        if (json.initial) {
          series.upperQtyBound?.setData(upperQtyBound);
          series.lowerQtyBound?.setData(lowerQtyBound);
          if (!hideExecQty) {
            series.execQty?.setData(execQty);
          } else {
            // hideExecQty can toggle from false -> true on the same chart instance; explicitly
            // clear exec data so previously plotted values are not left visible.
            series.execQty?.setData([]);
          }
        } else {
          for (let i = 0; i < analytics.length; i++) {
            series.upperQtyBound?.update(upperQtyBound[i]);
            series.lowerQtyBound?.update(lowerQtyBound[i]);
            if (!hideExecQty) {
              series.execQty?.update(execQty[i]);
            }
          }

          if (hideExecQty) {
            series.execQty?.setData([]);
          }
        }

        ensureSeriesSpacing(series, requestedResolution);
        updateZoom(series, false);

        lastValues.current = {
          upperQtyBound: upperQtyBound.at(-1)?.value,
          lowerQtyBound: lowerQtyBound.at(-1)?.value,
          execQty: execQty.at(-1)?.value,
          predExecQty: predExecQty?.at(-1)?.value,
        };

        if (!isMovingCrosshair.current) {
          setLegendValues(lastValues.current);
        }
      });

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [
    chart,
    legOrParentAnalyticsObservable,
    legIndex,
    predBounds,
    security,
    series,
    theme,
    trajOrder,
    requestedResolution,
    updateZoom,
    shouldInvalidatePred,
    hideExecQty,
    staticBounds,
  ]);

  useEffect(() => {
    if (!chart) {
      return;
    }

    const handleCrosshairMoved = (params: MouseEventParams) => {
      if (params.point) {
        isMovingCrosshair.current = true;

        const getSeriesData = (line?: ISeriesApi<'Line'>): LineData | undefined => {
          if (!line) {
            return;
          }
          return params.seriesData.get(line) as LineData | undefined;
        };

        setLegendValues(prev => {
          const upperQtyBoundValue = getSeriesData(series.upperQtyBound);
          const lowerQtyBoundValue = getSeriesData(series.lowerQtyBound);
          const execQtyValue = getSeriesData(series.execQty);
          const predExecQtyValue = getSeriesData(series.predExecQty);
          return {
            upperQtyBound: upperQtyBoundValue?.value || prev.upperQtyBound,
            execQty: execQtyValue?.value || prev.execQty,
            lowerQtyBound: lowerQtyBoundValue?.value || prev.lowerQtyBound,
            predExecQty: predExecQtyValue?.value || prev.predExecQty,
          };
        });
      } else {
        isMovingCrosshair.current = false;
        setLegendValues(lastValues.current);
      }
    };
    chart.subscribeCrosshairMove(handleCrosshairMoved);

    return () => {
      chart.unsubscribeCrosshairMove(handleCrosshairMoved);
    };
  }, [chart, series]);

  useEffect(() => {
    const sub = zoomSubject?.subscribe(() => updateZoom(series, true));
    return () => sub?.unsubscribe();
  }, [chart, zoomSubject, series, updateZoom]);

  const handleClickLegend = useCallback(
    (...keys: (keyof typeof series)[]) => {
      let updated = false;

      keys.forEach(key => {
        if (series[key] == null) {
          logger.warn(`Series ${key} not found`);
          return;
        }

        const currentVisibility = series[key].options().visible;
        series[key].applyOptions({ visible: !currentVisibility });
        updated = true;
      });

      if (updated) {
        setSeries(prev => ({ ...prev }));
      }
    },
    [series]
  );

  const isMouseDown = useRef(false);
  useEventListener(
    'mousemove',
    () => {
      if (isMouseDown.current) {
        const timeScale = chart?.timeScale();
        timeScale && onVisibleRangeChange(timeScale.getVisibleLogicalRange());
      }
    },
    ref
  );
  useEventListener(
    'wheel',
    () => {
      const timeScale = chart?.timeScale();
      timeScale && onVisibleRangeChange(timeScale.getVisibleLogicalRange());
    },
    ref
  );
  useEventListener(
    'mousedown',
    () => {
      isMouseDown.current = true;
    },
    ref
  );
  useEventListener('mouseup', () => {
    isMouseDown.current = false;
  });

  useEffect(() => {
    if (clientWidth && resizeAware && chart) {
      chart.applyOptions({ width: clientWidth });
    }
  }, [chart, clientWidth, resizeAware]);

  if (security == null) {
    return null;
  }

  return (
    <Stack gap="spacing16">
      {predError && (
        <Alert variant={AlertVariants.Warning} dismissable={false} size={FormControlSizes.Tiny}>
          <Stack gap="spacing2">
            <Text>Trajectory Prediction Unavailable</Text>
            <Text color="colorTextSubtle">{predError.message}</Text>
          </Stack>
        </Alert>
      )}
      <ChartLegend>
        <ChartLegendItem
          onClick={() => handleClickLegend('upperQtyBound')}
          visible={!!series.upperQtyBound?.options().visible}
        >
          <ChartLegendLabel color={colors.upperQtyBound}>Upper Qty Bound</ChartLegendLabel>
          <ChartLegendValue>
            {legendValues.upperQtyBound == null ? (
              'N/A'
            ) : (
              <InlineFormattedNumber number={legendValues.upperQtyBound} currency={currency} />
            )}
          </ChartLegendValue>
        </ChartLegendItem>
        {!hideExecQty && (
          <ChartLegendItem onClick={() => handleClickLegend('execQty')} visible={!!series.execQty?.options().visible}>
            <ChartLegendLabel color={colors.execQty}>Exec Qty</ChartLegendLabel>
            <ChartLegendValue>
              {legendValues.execQty == null ? (
                'N/A'
              ) : (
                <InlineFormattedNumber number={legendValues.execQty} currency={currency} align="right" />
              )}
            </ChartLegendValue>
          </ChartLegendItem>
        )}
        <ChartLegendItem
          onClick={() => handleClickLegend('lowerQtyBound')}
          visible={!!series.lowerQtyBound?.options().visible}
        >
          <ChartLegendLabel color={colors.lowerQtyBound}>Lower Qty Bound</ChartLegendLabel>
          <ChartLegendValue>
            {legendValues.lowerQtyBound == null ? (
              'N/A'
            ) : (
              <InlineFormattedNumber number={legendValues.lowerQtyBound} currency={currency} align="right" />
            )}
          </ChartLegendValue>
        </ChartLegendItem>
        {canPredict && (
          <ChartLegendItem
            onClick={() => handleClickLegend('predExecQty', 'predLowerQtyBound', 'predUpperQtyBound')}
            visible={!!series.predExecQty?.options().visible}
          >
            <ChartLegendLabel color={colors.predExecQty}>Pred. Exec Qty</ChartLegendLabel>
            <ChartLegendValue>
              {legendValues.predExecQty == null ? (
                'N/A'
              ) : (
                <InlineFormattedNumber
                  number={legendValues.predExecQty}
                  increment={security.DefaultPriceIncrement}
                  currency={currency}
                  align="right"
                />
              )}
            </ChartLegendValue>
          </ChartLegendItem>
        )}
      </ChartLegend>
      <div style={{ position: 'relative' }}>
        <ChartWrapper ref={ref} />
        <ChartMeta>
          <Stack alignItems="flex-start" gap="spacing6">
            {resolution} / {timestamp}
            {isPredLoading && (
              <IndicatorBadge
                data-testid="hedging-button-loading"
                children={
                  <>
                    <Spinner size={8} />
                    <Text ml="spacing4">Predictions Loading</Text>
                  </>
                }
                variant={IndicatorBadgeVariants.Default}
              />
            )}
          </Stack>
        </ChartMeta>
      </div>
    </Stack>
  );
};
